import { v4 as uuidv4 } from 'uuid';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { addMinutes, format, isAfter, isBefore, parse } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { db } from '../firebaseConfig';

export const formatLocalDate = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const calcEndTime = (startTime, duration) => {
  if (!startTime || !duration) return '';
  const [hours, minutes] = startTime.split(':').map(Number);
  const totalMinutes = hours * 60 + minutes + Number(duration);
  const endHours = String(Math.floor(totalMinutes / 60) % 24).padStart(2, '0');
  const endMinutes = String(totalMinutes % 60).padStart(2, '0');
  return `${endHours}:${endMinutes}`;
};

export const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

export const hasAppointmentConflict = (appointments, appointment) => {
  const nextStart = timeToMinutes(appointment.startTime);
  const nextEnd = nextStart + Number(appointment.duration);

  return appointments.some((existing) => {
    if (existing.canceled) return false;
    if (existing.id && existing.id === appointment.id) return false;
    if (existing.employeeId !== appointment.employeeId) return false;
    if (existing.date !== appointment.date) return false;

    const existingStart = timeToMinutes(existing.startTime);
    const existingEnd = existingStart + Number(existing.duration || 0);
    return nextStart < existingEnd && nextEnd > existingStart;
  });
};

export const buildActivityText = (activity) => {
  const subject = activity.customerName || 'Ein Termin';
  const datePart = activity.appointmentDate || activity.date || '';
  const timePart = activity.startTime ? ` um ${activity.startTime}` : '';

  switch (activity.type) {
    case 'appointment_created':
      return `${subject} wurde für ${datePart}${timePart} gebucht.`;
    case 'appointment_created_admin':
      return `${subject} wurde intern für ${datePart}${timePart} angelegt.`;
    case 'appointment_canceled':
      return `${subject} wurde für ${datePart}${timePart} storniert.`;
    case 'appointment_updated':
      return `${subject} wurde aktualisiert.`;
    default:
      return activity.message || 'Aktivität erfasst.';
  }
};

export const createActivityLog = async (tenantId, data) => {
  if (!tenantId) return;
  await addDoc(collection(db, 'tenants', tenantId, 'activityLog'), {
    ...data,
    createdAt: serverTimestamp(),
  });
};

export const loadAdminBaseData = async (tenantId) => {
  const [employeesSnap, typesSnap] = await Promise.all([
    getDocs(collection(db, 'tenants', tenantId, 'employees')),
    getDocs(collection(db, 'tenants', tenantId, 'appointmentTypes')),
  ]);

  return {
    employees: employeesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
    appointmentTypes: typesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
  };
};

export const loadAppointmentsForDate = async (tenantId, date) => {
  const appointmentsQuery = query(
    collection(db, 'tenants', tenantId, 'appointments'),
    where('date', '==', date)
  );
  const snapshot = await getDocs(appointmentsQuery);
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
};

export const loadAppointmentsForRange = async (tenantId, startDate, endDate) => {
  const appointmentsQuery = query(
    collection(db, 'tenants', tenantId, 'appointments'),
    where('date', '>=', startDate),
    where('date', '<=', endDate)
  );
  const snapshot = await getDocs(appointmentsQuery);
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
};

export const loadAvailabilityDataForDate = async (tenantId, date) => {
  const [availabilitySnap, legacyAvailabilitySnap, absencesSnap, appointments] = await Promise.all([
    getDocs(collection(db, 'tenants', tenantId, 'availability')),
    getDocs(collection(db, 'tenants', tenantId, 'availabilities')),
    getDocs(
      query(
        collection(db, 'tenants', tenantId, 'absences'),
        where('date', '==', date)
      )
    ),
    loadAppointmentsForDate(tenantId, date),
  ]);

  const availabilitiesById = new Map();
  availabilitySnap.docs.forEach((docSnap) => {
    availabilitiesById.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
  });
  legacyAvailabilitySnap.docs.forEach((docSnap) => {
    if (!availabilitiesById.has(docSnap.id)) {
      availabilitiesById.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    }
  });

  return {
    availabilities: Array.from(availabilitiesById.values()),
    absences: absencesSnap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })),
    appointments,
  };
};

const weekdayAliases = {
  Monday: ['Monday', 'Montag', 'Mo', 'monday', 'montag'],
  Tuesday: ['Tuesday', 'Dienstag', 'Di', 'tuesday', 'dienstag'],
  Wednesday: ['Wednesday', 'Mittwoch', 'Mi', 'wednesday', 'mittwoch'],
  Thursday: ['Thursday', 'Donnerstag', 'Do', 'thursday', 'donnerstag'],
  Friday: ['Friday', 'Freitag', 'Fr', 'friday', 'freitag'],
  Saturday: ['Saturday', 'Samstag', 'Sa', 'saturday', 'samstag'],
  Sunday: ['Sunday', 'Sonntag', 'So', 'sunday', 'sonntag'],
};

const availabilityMatchesWeekday = (availability, dayOfWeek) => {
  const weekdays = availability.weekdays || availability.days || [];
  const aliases = weekdayAliases[dayOfWeek] || [dayOfWeek];
  return weekdays.some((weekday) => aliases.includes(weekday));
};

export const computeAvailableSlots = ({
  date,
  appointmentTypeId,
  employeeId,
  appointmentTypes,
  employees,
  availabilities,
  absences,
  appointments,
}) => {
  if (!date || !appointmentTypeId) return [];

  const day = parse(date, 'yyyy-MM-dd', new Date());
  const dayOfWeek = format(day, 'EEEE', { locale: enUS });
  const selectedType = appointmentTypes.find((type) => type.id === appointmentTypeId);
  const duration = Number(selectedType?.duration || 60);
  const allowedEmployeeIds = selectedType?.assignedEmployees || [];
  const selectedEmployeeIds = employeeId
    ? [employeeId]
    : employees
        .map((employee) => employee.id)
        .filter((id) => allowedEmployeeIds.length === 0 || allowedEmployeeIds.includes(id));

  const slots = [];

  for (const currentEmployeeId of selectedEmployeeIds) {
    const employee = employees.find((item) => item.id === currentEmployeeId);
    const isAbsent = absences.some((absence) => absence.employeeId === currentEmployeeId);
    if (isAbsent) continue;

    const dailyAvailabilities = availabilities.filter((availability) => {
      if (availability.employeeId !== currentEmployeeId) return false;
      if (!availabilityMatchesWeekday(availability, dayOfWeek)) return false;
      return true;
    });

    const dailyAppointments = appointments
      .filter((appointment) => appointment.employeeId === currentEmployeeId && appointment.canceled !== true)
      .sort((a, b) => timeToMinutes(a.startTime) - timeToMinutes(b.startTime));

    for (const availability of dailyAvailabilities) {
      let freeSegments = [
        {
          start: parse(availability.startTime, 'HH:mm', day),
          end: parse(availability.endTime, 'HH:mm', day),
        },
      ];

      for (const appointment of dailyAppointments) {
        const appointmentStart = parse(appointment.startTime, 'HH:mm', day);
        const appointmentEnd = addMinutes(appointmentStart, Number(appointment.duration || 0));

        freeSegments = freeSegments.flatMap((segment) => {
          if (segment.end <= appointmentStart || segment.start >= appointmentEnd) {
            return [segment];
          }

          const nextSegments = [];
          if (isBefore(segment.start, appointmentStart)) {
            nextSegments.push({ start: segment.start, end: appointmentStart });
          }
          if (isAfter(segment.end, appointmentEnd)) {
            nextSegments.push({ start: appointmentEnd, end: segment.end });
          }
          return nextSegments;
        });
      }

      for (const segment of freeSegments) {
        let slotStart = segment.start;
        while (addMinutes(slotStart, duration) <= segment.end) {
          const startTime = format(slotStart, 'HH:mm');
          slots.push({
            employeeId: currentEmployeeId,
            employeeName: employee?.name || 'Unbekannt',
            startTime,
            endTime: format(addMinutes(slotStart, duration), 'HH:mm'),
            duration,
          });
          slotStart = addMinutes(slotStart, 30);
        }
      }
    }
  }

  return Array.from(
    new Map(slots.map((slot) => [`${slot.employeeId}-${slot.startTime}`, slot])).values()
  ).sort((a, b) => {
    const timeDiff = timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
    if (timeDiff !== 0) return timeDiff;
    return a.employeeName.localeCompare(b.employeeName);
  });
};

export const loadRecentActivities = async (tenantId, maxItems = 8) => {
  const activityQuery = query(
    collection(db, 'tenants', tenantId, 'activityLog'),
    orderBy('createdAt', 'desc'),
    limit(maxItems)
  );
  const snapshot = await getDocs(activityQuery);
  return snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
};

export const deleteAppointmentWithActivityLogs = async (tenantId, appointmentId) => {
  const appointmentRef = doc(db, 'tenants', tenantId, 'appointments', appointmentId);
  const activityQuery = query(
    collection(db, 'tenants', tenantId, 'activityLog'),
    where('appointmentId', '==', appointmentId)
  );
  const activitySnapshot = await getDocs(activityQuery);

  const batch = writeBatch(db);
  batch.delete(appointmentRef);
  activitySnapshot.docs.forEach((activityDoc) => {
    batch.delete(activityDoc.ref);
  });
  await batch.commit();

  return activitySnapshot.size;
};

export const createAdminAppointment = async (tenantId, form, appointmentTypes, existingAppointments = []) => {
  const type = appointmentTypes.find((item) => item.id === form.appointmentTypeId);
  const duration = Number(form.duration || type?.duration || 60);
  const appointment = {
    date: form.date,
    startTime: form.startTime,
    duration,
    employeeId: form.employeeId,
    appointmentTypeId: form.appointmentTypeId,
    customerName: form.customerName.trim(),
    customerEmail: form.customerEmail.trim(),
    customerPhoneNumber: form.customerPhoneNumber.trim(),
    notes: form.notes.trim(),
    canceled: false,
    cancelToken: uuidv4(),
    bookedAt: new Date(),
    bookingSource: 'admin',
  };

  if (hasAppointmentConflict(existingAppointments, appointment)) {
    throw new Error('Dieser Zeitraum überschneidet sich mit einem bestehenden Termin.');
  }

  const appointmentRef = await addDoc(
    collection(db, 'tenants', tenantId, 'appointments'),
    appointment
  );

  await createActivityLog(tenantId, {
    type: 'appointment_created_admin',
    appointmentId: appointmentRef.id,
    appointmentDate: appointment.date,
    startTime: appointment.startTime,
    customerName: appointment.customerName,
    employeeId: appointment.employeeId,
    appointmentTypeId: appointment.appointmentTypeId,
    message: buildActivityText({
      type: 'appointment_created_admin',
      ...appointment,
      appointmentDate: appointment.date,
    }),
  });

  return { id: appointmentRef.id, ...appointment };
};
